import React from 'react';
import { xcss, Box, Stack } from '@atlaskit/primitives';
import { Card } from '@atlaskit/media-card';
import { token } from '@atlaskit/tokens';
import type { FileIdentifier } from '@atlaskit/media-client';
import type { MediaClientConfig } from '@atlaskit/media-core';
import Tooltip from '@atlaskit/tooltip';
import { Truncate } from '@atlaskit/media-ui/truncateText';
import { fg } from '@atlassian/jira-feature-gating';
import { mediaFeatureFlags } from '../../../../../../media-feature-flags.tsx';
import type { AttachmentActionItem } from '../../types.tsx';
import { GRID_ITEM_DIMENSIONS } from '../../constants.tsx';
import { DateFormatter } from './DateFormatter.tsx';
import { TitleBoxWrapper, TitleBoxHeader, TitleBoxFooter } from './TitleBox.tsx';

const { height, infoSectionHeight } = GRID_ITEM_DIMENSIONS;

const cardDimensions = {
	width: '100%',
	height: height - infoSectionHeight,
};

const placeholderText = ' ';

const MediaCardItem = ({
	item,
	mediaClientConfig,
	shouldEnableDownloadButton,
	attachmentIdentifiers,
}: {
	item: AttachmentActionItem;
	mediaClientConfig: MediaClientConfig;
	shouldEnableDownloadButton: boolean;
	attachmentIdentifiers: FileIdentifier[];
}) => (
	<Box xcss={mediaCardStyle} key={item.identifier.id}>
		<Stack>
			<Card
				mediaClientConfig={mediaClientConfig}
				mediaViewerItems={attachmentIdentifiers}
				useInlinePlayer={false}
				shouldOpenMediaViewer
				dimensions={cardDimensions}
				featureFlags={mediaFeatureFlags()}
				disableOverlay
				shouldEnableDownloadButton={
					!fg('block_attachment_download_on_data_export_dsp_rule') || shouldEnableDownloadButton
				}
				{...item}
				testId={`issue.views.issue-base.content.attachment.grid-view.attachment-id.${item.identifier.attachmentId}`}
			/>
			<TitleBoxWrapper>
				<TitleBoxHeader>
					<Tooltip tag="span" content={item.filename}>
						<Truncate text={item.filename || ''} startFixedChars={3} endFixedChars={4} />
					</Tooltip>
				</TitleBoxHeader>
				<TitleBoxFooter>
					<Box>
						{item.createdDate ? <DateFormatter timestamp={item.createdDate} /> : placeholderText}
					</Box>
				</TitleBoxFooter>
			</TitleBoxWrapper>
		</Stack>
	</Box>
);

const mediaCardStyle = xcss({
	// https://hello.atlassian.net/wiki/spaces/DST/pages/3225764567/Guide+for+Migrating+to+Spacing+Tokens#The-value-I%E2%80%99m-migrating-is-too-large-to-be-represented-with-a-token
	minWidth: '138px',
	width: '100%',
	height: '130px',
	cursor: 'pointer',
	borderRadius: token('border.radius.100'),
	overflow: 'hidden',
	border: `1px solid ${token('color.border')}`,
});

export default MediaCardItem;
